import { Formik } from "formik";
import { useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";

import {
  CancelButton,
  SelectBox,
  SelectTree,
  SubmitButton,
  TextArea,
  TextInput,
  ToggleButton,
} from "../Form";

import {
  isEmpty,
  isNothing,
  isValidDecimalSeparator,
  isValidFloat,
  numberFormatter,
  parseIntlNumber,
} from "../../utils";

import { validateSpecifications } from "../../validators";

import InstrumentationFullEmptyCalibrationHelp from "./InstrumentationFullEmptyCalibrationHelp";

import {
  instrumentationShape,
  instrumentationStatusesShape,
  instrumentationStatusShape,
  instrumentationTypeShape,
  instrumentationTypesShape,
  intlShape,
} from "../../shapes";
import More from "../More";
import SpecificationsInput from "../Specifications/SpecificationsInput";

type Props = {
  readonly instrumentation?: instrumentationShape;
  readonly instrumentationStatuses?: instrumentationStatusesShape;
  readonly instrumentationTypes?: instrumentationTypesShape;
  readonly defaultValues?: {
    tag?: string;
    status?: instrumentationStatusShape;
    type?: instrumentationTypeShape;
    accessibility?: string;
    criticality?: string;
    unit?: string;
    sensitivity?: string;
    mediumType?: string;
  };
  readonly assetFirmwareVersion?: string;
  readonly assetSensorBuildNumber?: string;
  readonly onSubmit?: $TSFixMeFunction;
  readonly cancelTarget?: string;
  readonly intl: intlShape;
  readonly instrumentationRules?: {
    get?: $TSFixMeFunction;
  };
  readonly collapseProperties?: boolean;
  readonly isUserProjectEngineer?: boolean;
};

export function InstrumentationForm({
  instrumentation,
  intl,
  instrumentationStatuses,
  instrumentationTypes,
  assetFirmwareVersion,
  assetSensorBuildNumber,
  defaultValues,
  onSubmit,
  cancelTarget,
  instrumentationRules,
  collapseProperties,
  isUserProjectEngineer = false,
}: Props) {
  const criticality = [
    { id: "low", name: intl.formatMessage({ id: "enum.criticality.low" }) },
    {
      id: "medium",
      name: intl.formatMessage({ id: "enum.criticality.medium" }),
    },
    { id: "high", name: intl.formatMessage({ id: "enum.criticality.high" }) },
    {
      id: "undefined",
      name: intl.formatMessage({ id: "enum.criticality.undefined" }),
    },
  ];
  const accessibility = [
    { id: "easy", name: intl.formatMessage({ id: "enum.accessibility.easy" }) },
    {
      id: "moderate",
      name: intl.formatMessage({ id: "enum.accessibility.moderate" }),
    },
    {
      id: "difficult",
      name: intl.formatMessage({ id: "enum.accessibility.difficult" }),
    },
    {
      id: "undefined",
      name: intl.formatMessage({ id: "enum.accessibility.undefined" }),
    },
  ];
  const sensitivity = [
    { id: "low", name: intl.formatMessage({ id: "enum.sensitivity.low" }) },
    {
      id: "medium",
      name: intl.formatMessage({ id: "enum.sensitivity.medium" }),
    },
    { id: "high", name: intl.formatMessage({ id: "enum.sensitivity.high" }) },
  ];

  const mediumTypes = [
    {
      id: "liquid",
      name: intl.formatMessage({ id: "enum.medium_type.liquid" }),
    },
    { id: "solid", name: intl.formatMessage({ id: "enum.medium_type.solid" }) },
  ];

  const initialValues = instrumentation
    ? {
        tag: instrumentation.tag,
        description: instrumentation.description,
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        status: instrumentationStatuses.find(
          (s: $TSFixMe) => s.name === instrumentation.statusName,
        ),
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        type: instrumentationTypes.find(
          (t: $TSFixMe) => t.name === instrumentation.typeName,
        ),
        accessibility: accessibility.find(
          (a) => a.id === (instrumentation as $TSFixMe).accessibility,
        ),
        criticality: criticality.find(
          (c) => c.id === instrumentation.criticality,
        ),
        criticalityQuality: (instrumentation as $TSFixMe).criticalityQuality,
        criticalityEnvironment: (instrumentation as $TSFixMe)
          .criticalityEnvironment,
        criticalitySafety: (instrumentation as $TSFixMe).criticalitySafety,
        specialDemands: (instrumentation as $TSFixMe).specialDemands,
        environmentalCondition: (instrumentation as $TSFixMe)
          .environmentalCondition,
        unit: (instrumentation as $TSFixMe).unit
          ? (instrumentation as $TSFixMe).unit
          : // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
            instrumentationRules
              ?.get("fullEmptyCalibrationUnitOptions")
              ?.find((o: $TSFixMe) => o.id === "mm"),
        empty: (instrumentation as $TSFixMe).empty
          ? numberFormatter({
              useGrouping: false,
              maximumFractionDigits: 10,
            }).format((instrumentation as $TSFixMe).empty)
          : "",
        full: (instrumentation as $TSFixMe).full
          ? numberFormatter({
              useGrouping: false,
              maximumFractionDigits: 10,
            }).format((instrumentation as $TSFixMe).full)
          : "",
        blockDistance: (instrumentation as $TSFixMe).blockDistance
          ? numberFormatter({
              useGrouping: false,
              maximumFractionDigits: 10,
            }).format((instrumentation as $TSFixMe).blockDistance)
          : "-1",
        sensitivity:
          sensitivity.find(
            (s) => s.id === (instrumentation as $TSFixMe).sensitivity,
          ) || sensitivity.find((s) => s.id === "medium"),
        mediumType:
          mediumTypes.find(
            (s) => s.id === (instrumentation as $TSFixMe).mediumType,
          ) || mediumTypes.find((s) => s.id === "liquid"),
        specifications: (instrumentation as $TSFixMe).specifications,
      }
    : {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        tag: defaultValues.tag,
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        status: defaultValues.status
          ? // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            defaultValues.status
          : // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentationStatuses.find(
              (s: $TSFixMe) => s.code === "undefined",
            ),
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        type: defaultValues.type
          ? // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            defaultValues.type
          : // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            instrumentationTypes.find((t: $TSFixMe) => t.code === "undefined"),
        accessibility:
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          accessibility.find((a) => a.id === defaultValues.accessibility) ||
          accessibility.find((a) => a.id === "undefined"),
        criticality:
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          criticality.find((a) => a.id === defaultValues.criticality) ||
          criticality.find((a) => a.id === "undefined"),
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        unit: instrumentationRules
          ?.get("fullEmptyCalibrationUnitOptions")
          ?.find((o: $TSFixMe) => o.id === "mm"),
        blockDistance: "-1",
        sensitivity:
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          sensitivity.find((s) => s.id === defaultValues.sensitivity) ||
          sensitivity.find((s) => s.id === "medium"),
        mediumType:
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          mediumTypes.find((s) => s.id === defaultValues.mediumType) ||
          mediumTypes.find((s) => s.id === "liquid"),
      };

  const [customBlockDistance, disableCustomBlockDistance] = useState(
    initialValues.blockDistance !== "-1",
  );

  const validateForm = (values: $TSFixMe) => {
    const errors = {};

    if (isEmpty(values.tag) || isEmpty(values.tag.trim())) {
      (errors as $TSFixMe).tag = intl.formatMessage({
        id: "validation.tag.mandatory",
      });
    } else if (values.tag.length > 255) {
      (errors as $TSFixMe).tag = intl.formatMessage({
        id: "validation.tag.too_long",
      });
    }

    if (
      instrumentationRules &&
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      instrumentationRules?.get("needsFullEmptySettings")
    ) {
      if (!values.unit) {
        (errors as $TSFixMe).unit = intl.formatMessage({
          id: "validation.unit.mandatory",
        });
      }

      if (!values.empty) {
        (errors as $TSFixMe).empty = intl.formatMessage({
          id: "validation.empty.mandatory",
        });
      } else if (
        !isValidDecimalSeparator(values.empty) ||
        !isValidFloat(values.empty)
      ) {
        (errors as $TSFixMe).empty = intl.formatMessage({
          id: "validation.number.invalid",
        });
      } else {
        const emptyValue = parseIntlNumber(values.empty);
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        const range = instrumentationRules?.get(
          "fullEmptyRange",
          values.unit.id,
        );
        if (emptyValue < range.min) {
          (errors as $TSFixMe).empty = intl.formatMessage(
            { id: "validation.empty.smallerthanrange" },
            { min: `${range.min}${values.unit.id}` },
          );
        } else if (emptyValue > range.max) {
          (errors as $TSFixMe).empty = intl.formatMessage(
            { id: "validation.empty.biggerthanrange" },
            { max: `${range.max}${values.unit.id}` },
          );
        }
      }

      if (!values.full) {
        (errors as $TSFixMe).full = intl.formatMessage({
          id: "validation.full.mandatory",
        });
      } else if (
        !isValidDecimalSeparator(values.full) ||
        !isValidFloat(values.full)
      ) {
        (errors as $TSFixMe).full = intl.formatMessage({
          id: "validation.number.invalid",
        });
      } else {
        const fullValue = parseIntlNumber(values.full);
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        const range = instrumentationRules?.get(
          "fullEmptyRange",
          values.unit.id,
        );
        if (fullValue < range.min) {
          (errors as $TSFixMe).full = intl.formatMessage(
            { id: "validation.full.smallerthanrange" },
            { min: `${range.min}${values.unit.id}` },
          );
        } else if (fullValue > range.max) {
          (errors as $TSFixMe).full = intl.formatMessage(
            { id: "validation.full.biggerthanrange" },
            { max: `${range.max}${values.unit.id}` },
          );
        }
      }

      if (!values.blockDistance) {
        (errors as $TSFixMe).blockDistance = intl.formatMessage({
          id: "validation.block_distance.mandatory",
        });
      } else if (
        !isValidDecimalSeparator(values.blockDistance) ||
        !isValidFloat(values.blockDistance)
      ) {
        (errors as $TSFixMe).blockDistance = intl.formatMessage({
          id: "validation.number.invalid",
        });
      } else {
        const blockDistanceValue = parseIntlNumber(values.blockDistance);
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        const range = instrumentationRules?.get(
          "blockDistanceRange",
          values.unit.id,
        );
        if (blockDistanceValue !== -1) {
          if (blockDistanceValue < range.min) {
            (errors as $TSFixMe).blockDistance = intl.formatMessage(
              { id: "validation.block_distance.smallerthanrange" },
              { min: `${range.min}${values.unit.id}` },
            );
          } else if (blockDistanceValue > range.max) {
            (errors as $TSFixMe).blockDistance = intl.formatMessage(
              { id: "validation.block_distance.biggerthanrange" },
              { max: `${range.max}${values.unit.id}` },
            );
          }
        }
      }

      if (values.empty && values.full && values.blockDistance) {
        const emptyValue = parseIntlNumber(values.empty);
        const fullValue = parseIntlNumber(values.full);
        const blockDistanceValue = parseIntlNumber(values.blockDistance);
        if (fullValue > emptyValue) {
          (errors as $TSFixMe).full = intl.formatMessage({
            id: "validation.full.biggerthanempty",
          });
        }
        if (blockDistanceValue > emptyValue) {
          (errors as $TSFixMe).blockDistance = intl.formatMessage({
            id: "validation.block_distance.biggerthanempty",
          });
        }
      }
    }

    const specificationErrors = validateSpecifications(
      intl,
      values,
      isUserProjectEngineer,
    );

    if (specificationErrors) {
      (errors as $TSFixMe).specifications = specificationErrors;
    }

    return errors;
  };

  const mediumTypeSetting = (formProps: $TSFixMe) =>
    instrumentationRules &&
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    instrumentationRules?.get("needsMediumType") &&
    // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    instrumentationRules?.get(
      "isCapableOfMeasuringSolids",
      assetFirmwareVersion,
      assetSensorBuildNumber,
    ) ? (
      <SelectBox
        {...formProps}
        id="medium-type"
        name="mediumType"
        labelKey="name"
        label={intl.formatMessage({ id: "label.medium_type" })}
        options={mediumTypes}
        disableOptionsSort
        required
      />
    ) : null;

  const fullEmptySettings = (formProps: $TSFixMe) => {
    const onBDAutomaticChange = () => {
      const newCustomValue = !customBlockDistance;
      if (!newCustomValue) {
        formProps.setFieldValue("blockDistance", "-1");
        /* eslint-disable no-param-reassign */
        formProps.errors.blockDistance = null;
      } else {
        formProps.setFieldValue("blockDistance", "");
        formProps.errors.blockDistance = null;
      }
      disableCustomBlockDistance(newCustomValue);
    };

    return instrumentationRules &&
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      instrumentationRules?.get("needsFullEmptySettings") ? (
      <>
        <h3>
          <FormattedMessage id="label.full_empty_calibration" />
          <InstrumentationFullEmptyCalibrationHelp />
        </h3>
        <SelectBox
          {...formProps}
          id="instrumentation-unit"
          name="unit"
          label={intl.formatMessage({ id: "label.unit" })}
          // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          options={instrumentationRules?.get("fullEmptyCalibrationUnitOptions")}
          disableOptionsSort
          required
        />
        <TextInput
          {...formProps}
          id="instrumentation-empty"
          name="empty"
          min="0"
          label={intl.formatMessage({ id: "label.empty" })}
          required
        />
        <TextInput
          {...formProps}
          id="instrumentation-full"
          name="full"
          min="0"
          label={intl.formatMessage({ id: "label.full" })}
          required
        />
        <label htmlFor="block-distance-section">{`${intl.formatMessage({ id: "label.block_distance" })}*`}</label>
        <div id="block-distance-section">
          <input
            id="block-distance-automatic-checkbox"
            name="blockDistanceAutomatic"
            type="checkbox"
            className="unchecked"
            checked={!customBlockDistance}
            onChange={onBDAutomaticChange}
          />
          <label
            htmlFor="block-distance-automatic-checkbox"
            className="inline-display"
          >
            {intl.formatMessage({ id: "label.automatic" })}
          </label>
          <TextInput
            {...formProps}
            id="instrumentation-block-distance"
            name="blockDistance"
            min="-1"
            hidden={!customBlockDistance}
            hideLabel={!customBlockDistance}
            placeholder={intl.formatMessage({ id: "label.block_distance" })}
            customClass="inline-text-input"
          />
        </div>
        <SelectBox
          {...formProps}
          id="instrumentation-sensitivity"
          name="sensitivity"
          label={intl.formatMessage({ id: "label.sensitivity" })}
          options={sensitivity}
          disableOptionsSort
          required
        />
      </>
    ) : null;
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { handleSubmit, setStatus, isSubmitting } = formProps;

    const beforeHandleSubmit = (event: $TSFixMe) => {
      setStatus("submitted");
      handleSubmit(event);
    };

    const properties = (
      <>
        <SelectBox
          {...formProps}
          id="accessibility"
          name="accessibility"
          label={intl.formatMessage({ id: "label.accessibility" })}
          options={accessibility}
          disableOptionsSort
          required
        />
        <SelectBox
          {...formProps}
          id="tag-criticality"
          name="criticality"
          label={intl.formatMessage({ id: "label.criticality" })}
          options={criticality}
          disableOptionsSort
          required
        />
        <div id="tag-labels" className="form-group">
          <label htmlFor="edit-tag-labels">
            {intl.formatMessage({ id: "label.tag_labels" })}
          </label>
          <ToggleButton
            {...formProps}
            id="criticality-environment"
            name="criticalityEnvironment"
            text={intl.formatMessage({ id: "label.criticality_env" })}
            initial={false}
          />
          <ToggleButton
            {...formProps}
            id="criticality-quality"
            name="criticalityQuality"
            text={intl.formatMessage({ id: "label.criticality_quality" })}
            initial={false}
          />
          <ToggleButton
            {...formProps}
            id="criticality-safety"
            name="criticalitySafety"
            text={intl.formatMessage({ id: "label.criticality_safety" })}
            initial={false}
          />
          <ToggleButton
            {...formProps}
            id="special-demands"
            name="specialDemands"
            text={intl.formatMessage({ id: "label.special_demands" })}
            initial={false}
          />
          <ToggleButton
            {...formProps}
            id="environmental-condition"
            name="environmentalCondition"
            text={intl.formatMessage({ id: "label.environmental_condition" })}
            initial={false}
          />
        </div>
      </>
    );
    return (
      <form onSubmit={beforeHandleSubmit} noValidate>
        <TextInput
          {...formProps}
          id="tag-name"
          name="tag"
          label={intl.formatMessage({ id: "label.tag" })}
          required
        />
        <SelectBox
          {...formProps}
          id="tag-status"
          name="status"
          label={intl.formatMessage({ id: "label.operation_mode" })}
          options={instrumentationStatuses}
          required
        />
        <SelectTree
          {...formProps}
          id="tag-type"
          name="type"
          label={intl.formatMessage({ id: "label.type" })}
          options={instrumentationTypes}
          required
        />
        <TextArea
          {...formProps}
          id="tag-description"
          name="description"
          label={intl.formatMessage({ id: "label.description" })}
        />
        <SpecificationsInput
          {...formProps}
          specificationKeysEndpoint="/instrumentation/specification_keys"
          isUserProjectEngineer={isUserProjectEngineer}
        />
        {mediumTypeSetting(formProps)}
        {fullEmptySettings(formProps)}
        {collapseProperties ? (
          // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; }' is not a... Remove this comment to see the full error message
          <More id="more-properties">{properties}</More>
        ) : (
          properties
        )}
        <div className="btn-group">
          <SubmitButton
            id="instrumentation-submit"
            onClick={beforeHandleSubmit}
            fetching={isSubmitting}
            disabled={!formProps.dirty && isNothing(defaultValues?.tag)}
          />
          <CancelButton
            id="instrumentation-cancel"
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; disabled: boolean; target: str... Remove this comment to see the full error message
            disabled={false}
            target={cancelTarget}
          />
        </div>
      </form>
    );
  };

  return (
    <Formik
      id="formik-instrumentation"
      initialValues={initialValues}
      enableReinitialize
      // @ts-expect-error TS(2322) FIXME: Type '$TSFixMeFunction | undefined' is not assigna... Remove this comment to see the full error message
      onSubmit={onSubmit}
      validate={validateForm}
    >
      {renderForm}
    </Formik>
  );
}

export default injectIntl(InstrumentationForm);
